import React from "react";

import {
  useAudioSeekGestures,
  type AudioPlayerControls,
} from "@fitness-app/client-shared/features/chat/use-audio-player";
import { cn } from "@fitness-app/utils/src/styles/cn";

interface AudioWaveformProps extends Pick<AudioPlayerControls, "progress" | "seek"> {
  /** Bar heights 0..1, see `buildAudioWaveBars`. */
  bars: number[];
  playedClassName: string;
  unplayedClassName: string;
  className?: string;
}

/** The scrubbable bar strip shared by a sent voice message and the recorder's preview. */
export const AudioWaveform = ({
  bars,
  progress,
  seek,
  playedClassName,
  unplayedClassName,
  className,
}: AudioWaveformProps): React.ReactElement => {
  const seekGestures = useAudioSeekGestures({ progress, seek });

  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label="Postęp odtwarzania"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      className={cn(
        "flex h-8 flex-1 cursor-pointer touch-none items-center gap-[2px] rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current",
        className,
      )}
      {...seekGestures}
    >
      {bars.map((bar, index) => (
        <span
          // Bars are positional; the envelope never reorders.
          key={index}
          className={cn(
            "min-w-[2px] flex-1 rounded-full transition-[opacity,background-color] duration-100",
            index / bars.length < progress ? playedClassName : unplayedClassName,
          )}
          style={{ height: `${Math.round(bar * 100)}%` }}
        />
      ))}
    </div>
  );
};
